// Shared API client bridge for SaaS (non-breaking exports)
// Use local shared client to ensure Vercel build doesn't need monorepo context
import { createClient } from './sharedClient.js';
import { mockApiService, shouldUseMockData } from '../services/mockData.js';
import logger from '../utils/logger';
import {
  MetricsSchema,
  AgentsResponseSchema,
  ScrapersResponseSchema,
  AlertsResponseSchema,
  ProductsSearchSchema,
  BarcodeScanSchema,
} from './schemas.js';

// SECURITY: Use HTTPS for production to encrypt data in transit
// CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const isTestEnv =
  env.MODE === 'test' ||
  env.VITEST === 'true' ||
  env.VITEST === true ||
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test');

const baseURL = isTestEnv
  ? 'http://localhost:8000'
  : (
      env.VITE_API_BASE_URL ||
      env.VITE_API_URL ||
      'https://apps.quantrolix.ai/api/quantrolix'
    ).trim();

const realClient = createClient({ baseURL });
export const apiBase = realClient.baseURL;

async function parseOrFallback(schema, data, { source, fallback }) {
  try {
    return schema.parse(data);
  } catch (error) {
    logger.warn(`Schema validation failed for ${source}:`, error);
    if (typeof fallback === 'function') {
      const fallbackData = await fallback();
      return schema.parse(fallbackData);
    }
    throw error;
  }
}

// Enhanced client with fallback to mock data
export const client = {
  baseURL: realClient.baseURL,

  health: async () => {
    try {
      return await realClient.health();
    } catch (error) {
      if (shouldUseMockData(error)) {
        logger.warn('API rate limited, using mock data for health check');
        return await mockApiService.health();
      }
      throw error;
    }
  },

  metrics: async () => {
    try {
      const data = await realClient.metrics();
      return await parseOrFallback(MetricsSchema, data, {
        source: 'metrics',
        fallback: async () => mockApiService.metrics(),
      });
    } catch (error) {
      if (shouldUseMockData(error)) {
        logger.warn('API rate limited, using mock data for metrics');
        return MetricsSchema.parse(await mockApiService.metrics());
      }
      throw error;
    }
  },

  alerts: {
    recent: async () => {
      try {
        const alerts = await realClient.alerts.recent();
        return AlertsResponseSchema.parse(alerts);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, using mock data for alerts');
          return AlertsResponseSchema.parse(await mockApiService.alerts.recent());
        }
        throw error;
      }
    }
  },

  agents: {
    status: async () => {
      try {
        const data = await realClient.agents.status();
        return AgentsResponseSchema.parse(data);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, using mock data for agents');
          return AgentsResponseSchema.parse(await mockApiService.agents.status());
        }
        throw error;
      }
    }
  },

  scrapers: {
    list: async () => {
      try {
        const data = await realClient.scrapers.list();
        return ScrapersResponseSchema.parse(data);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, using mock data for scrapers');
          return ScrapersResponseSchema.parse(await mockApiService.scrapers.list());
        }
        throw error;
      }
    },
    start: async (id) => {
      try {
        return await realClient.scrapers.start(id);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating scraper start');
          return { success: true, id, status: 'simulated' };
        }
        throw error;
      }
    },
    stop: async (id) => {
      try {
        return await realClient.scrapers.stop(id);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating scraper stop');
          return { success: true, id, status: 'simulated' };
        }
        throw error;
      }
    },
  },

  sellerAccounts: {
    list: async () => {
      try {
        return await realClient.sellerAccounts.list();
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, returning mock seller accounts');
          return { success: true, accounts: [] };
        }
        throw error;
      }
    },
    create: async (payload) => {
      try {
        return await realClient.sellerAccounts.create(payload);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating seller account create');
          return {
            success: true,
            account: {
              id: Date.now(),
              platform: payload.platform,
              account_name: payload.account_name,
              seller_id: payload.seller_id ?? null,
              is_active: true,
              credential_masks: {
                api_key: '************0000',
                api_secret: '************0000',
                access_token: '************0000',
                refresh_token: '************0000',
              },
              last_sync: null,
              last_sync_status: 'simulated',
            },
          };
        }
        throw error;
      }
    },
    update: async (id, payload) => {
      try {
        return await realClient.sellerAccounts.update(id, payload);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating seller account update');
          return {
            success: true,
            account: {
              id,
              ...payload,
              credential_masks: {
                api_key: '************0000',
                api_secret: '************0000',
                access_token: '************0000',
                refresh_token: '************0000',
              },
            },
          };
        }
        throw error;
      }
    },
    remove: async (id) => {
      try {
        return await realClient.sellerAccounts.remove(id);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating seller account delete');
          return { success: true };
        }
        throw error;
      }
    },
    test: async (id) => {
      try {
        return await realClient.sellerAccounts.test(id);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating seller account test');
          return {
            success: true,
            status: 'simulated',
            timestamp: new Date().toISOString(),
          };
        }
        throw error;
      }
    },
    sync: async (id) => {
      try {
        return await realClient.sellerAccounts.sync(id);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, simulating seller account sync');
          return {
            success: true,
            message: 'Sync queued (simulated)',
            timestamp: new Date().toISOString(),
          };
        }
        throw error;
      }
    },
  },

  products: {
    search: async (query, options) => {
      try {
        const data = await realClient.products.search(query, options);
        return ProductsSearchSchema.parse(data);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, using mock data for product search');
          return ProductsSearchSchema.parse(await mockApiService.products.search(query, options));
        }
        throw error;
      }
    },
    barcodeScan: async (payload) => {
      try {
        const data = await realClient.products.barcodeScan(payload);
        return BarcodeScanSchema.parse(data);
      } catch (error) {
        if (shouldUseMockData(error)) {
          logger.warn('API rate limited, using mock data for barcode scan');
          return BarcodeScanSchema.parse(await mockApiService.products.barcodeScan(payload));
        }
        throw error;
      }
    }
  }
};

export async function fetchJson(path, opts = {}) {
  // Delegate to fetch with client baseURL; keep signature
  const url = path.startsWith('http') ? path : `${client.baseURL}${path}`;
  const controller = new AbortController();
  const timeout = opts.timeout ?? 8000;
  const timer = setTimeout(() => controller.abort(), timeout);
  const token = localStorage.getItem('auth_token');
  const res = await fetch(url, {
    method: opts.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: controller.signal,
    credentials: 'include',
    mode: 'cors',
  });
  clearTimeout(timer);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${res.statusText} ${text}`);
  }
  return res.json();
}

// Convenience endpoints used by SaaS pages, proxied to shared client
export const api = {
  metrics: () => client.metrics(),
  alertsRecent: () => client.alerts.recent(),
  agentsStatus: () => client.agents.status(),
  productsSearch: (query, options) => client.products.search(query, options),
  barcodeScan: (payload) => client.products.barcodeScan(payload),
  marketplaceSearch: (asin, options) => realClient.marketplace.search(asin, options),
  createScan: (asin, options) => realClient.scans.create(asin, options),
  getScanStatus: (scanId) => realClient.scans.status(scanId),
  scrapers: {
    list: () => client.scrapers.list(),
    start: (id) => client.scrapers.start(id),
    stop: (id) => client.scrapers.stop(id),
  },
  sellerAccounts: {
    list: () => client.sellerAccounts.list(),
    create: (payload) => client.sellerAccounts.create(payload),
    update: (id, payload) => client.sellerAccounts.update(id, payload),
    remove: (id) => client.sellerAccounts.remove(id),
    test: (id) => client.sellerAccounts.test(id),
    sync: (id) => client.sellerAccounts.sync(id),
  },
};
